import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  HostListener,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { UserPrefsService } from '../../core/services/user-prefs.service';
import { AuthService } from '../../core/services/auth.service';
import { Subject, takeUntil, filter } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { NotificationService } from '../../core/services/notification.service';
import { NotificationPanelComponent } from './notification-panel/notification-panel.component';
import { SearchBarComponent } from './search-bar/search-bar.component';
import { WaffleMenuComponent } from './waffle-menu/waffle-menu.component';
import { AuthService as RealAuthService } from '../../api/services/auth.service';
import { VersionService, AppVersion } from '../../api/services/version.service';
import { InitialsService } from '../../helpers/initial.service';
import { ThemeService } from '../../api/services/theme.service';
import { PosthogService } from '../../api/services/posthog.service';
import { FORM_ID } from '../../helpers/form-id.enum';
import { Router, NavigationEnd } from '@angular/router';
import { ModalRendererComponent } from '../../shared/widgets/modal-renderer/modal-renderer.component';
import { SettingsComponent } from '../../features/admin/settings/settings.component';
import { CommonModule, Location } from '@angular/common';
import { CommonService } from '../../api/services/common.service';
import { GraphServiceService } from '../../api/services/graph-service.service';
import { ProfilePhotoService } from '../../helpers/profile-photo.service';
import { environment } from '../../../environments/environment';
import { getAccessValue, getRoleAccess } from '../../helpers/rbac.helper';

@Component({
  selector: 'ec-top-bar',
  imports: [
    RouterLink,
    NotificationPanelComponent,
    // SearchBarComponent,
    WaffleMenuComponent,
    ModalRendererComponent,
    SettingsComponent,
    CommonModule,
  ],
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.topbar--waffle-open]': 'waffleOpen()',
  },
})
export class TopBarComponent implements OnInit, OnDestroy {
  protected readonly prefs = inject(UserPrefsService);
  protected readonly auth = inject(AuthService);
  protected readonly notifs = inject(NotificationService);

  readonly waffleOpen = signal(false);
  readonly notifOpen = signal(false);
  userMenuOpen = false;
  readonly bookmarkMenuOpen = signal(false);
  bookmarks: any[] = [];
  bookmarksLoading = false;
  faviconCache = new Map<string, string>();

  protected readonly realAuth = inject(RealAuthService);
  protected readonly versionService = inject(VersionService);
  protected readonly initialsService = inject(InitialsService);
  protected readonly themeService = inject(ThemeService);
  protected readonly posthog = inject(PosthogService);
  protected readonly router = inject(Router);
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly commonService = inject(CommonService);
  protected readonly graphService = inject(GraphServiceService);
  protected readonly profilePhotoService = inject(ProfilePhotoService);
  private readonly location = inject(Location);
  protected readonly toaster = inject(ToastrService);

  @ViewChild('modalLogoFileInput')
  modalLogoFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('bookmarkModalRendererRef')
  bookmarkModalRendererRef!: ModalRendererComponent;

  showEditLogoModal = false;
  stagedLogoFile: File | null = null;
  stagedLogoUrl: string | null = null;
  isLogoUploading = false;
  isLogoDragOver = false;

  profileResponse: any;
  profilePhotoUrl: string | null = null;
  orgProfilePhotoUrl: string | null = null;
  isOrgLogoBroken = false;
  version?: AppVersion;
  aggregatedCount: number = 0;
  activeModalContext: any;
  modalTitle: any;
  activeBookmarkModalContext: any = null;
  bookmarkModalTitle: any = 'Add Bookmark';
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.closeAll();
      });

    this.versionService.getVersion().subscribe((v) => (this.version = v));
    this.getProfileDetails();
    this.loadGraphData(FORM_ID.GRAPHS);
    this.notifs.loadData(FORM_ID.NOTIFICATIONS_LIST);
    this.graphService.refreshGraph$.subscribe(() => {
      this.loadGraphData(FORM_ID.GRAPHS);
    });
    this.realAuth.profilePhotoUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((photoUrl?: any) => {
        if (photoUrl !== undefined) {
          if (photoUrl && typeof photoUrl === 'string') {
            if (photoUrl.startsWith('blob:') || photoUrl.startsWith('data:')) {
              this.profilePhotoUrl = photoUrl;
            } else {
              const separator = photoUrl.includes('?') ? '&' : '?';
              this.profilePhotoUrl = `${photoUrl}${separator}t=${new Date().getTime()}`;
            }
          } else {
            this.profilePhotoUrl = null;
          }
          if (this.profileResponse) {
            this.profileResponse.profilePhoto = photoUrl;
          }
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        } else {
          this.getProfileDetails();
        }
      });
  }

  getProfileDetails() {
    let token = localStorage.getItem('accessToken');
    this.realAuth.getProfile(token).subscribe({
      next: (data) => {
        this.profileResponse = data.data;
        this.isOrgLogoBroken = false;
        localStorage.setItem('pmSetup', this.profileResponse?.pmSetup);
        const subId = this.profileResponse?.subscriptionId;
        if (subId) {
          localStorage.setItem('subscriptionId', String(subId));
        }
        this.fetchProfilePhoto();
        this.fetchOrgProfilePhoto();
        this.cdr.markForCheck();
        this.cdr.detectChanges(); // Force immediate update
      },
      error: (err) => console.error('Error', err),
    });
  }

  fetchProfilePhoto(): void {
    if (this.profileResponse?.profilePhoto) {
      const photo = this.profileResponse.profilePhoto;
      const separator = photo.includes('?') ? '&' : '?';
      this.profilePhotoUrl = `${photo}${separator}t=${new Date().getTime()}`;
    } else {
      this.profilePhotoUrl = null;
    }
    this.cdr.markForCheck();
  }

  fetchOrgProfilePhoto(): void {
    if (this.profileResponse?.organizationProfilePhoto) {
      this.orgProfilePhotoUrl = `${this.profileResponse.organizationProfilePhoto}?t=${new Date().getTime()}`;
    } else {
      this.orgProfilePhotoUrl = null;
    }
    this.cdr.markForCheck();
  }

  handleOrgLogoError() {
    this.isOrgLogoBroken = true;
    this.cdr.markForCheck();
  }

  handleImageError(event: any) {
    event.target.src = './assets/media/icons/default-user-icon.png';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getInitials(name: string): string {
    if (!name) return '';
    return this.initialsService.getInitials(name);
  }

  loadGraphData(formId: any) {
    this.commonService.loadListWithOptions(formId).subscribe({
      next: (data) => {
        const graphData = data.data.records;
        graphData.forEach((graph: any) => {
          if (graph.code === 'X5J9CN') {
            const now = new Date();
            const startDate = new Date(
              now.getFullYear(),
              now.getMonth() - 12,
              1,
              0,
              0,
              0,
              0,
            );
            const endDate = new Date(
              now.getFullYear(),
              now.getMonth() + 1,
              0,
              23,
              59,
              59,
              999,
            );
            const confData = {
              cubeType: 'security_posture',
              grain: 'month',
              graphType: 'area',
              groupBy: ['timeKey'],
              metric: 'posture',
              source: 'snapshots',
              timeRange: {
                from: startDate.toISOString(),
                to: endDate.toISOString(),
              },
            };
            this.loadData(confData);
          }
        });
      },
      error: (err) => console.error('Error loading form JSON:', err),
    });
  }

  loadData(confData: any) {
    this.graphService.loadGraphData(confData).subscribe({
      next: (data) => {
        const responseData = data.data.apex;
        const { labels, series } = responseData;
        this.aggregatedCount = 0;
        if (series && series.length > 0) {
          if (series[0] && Array.isArray(series[0].data)) {
            const dataArray = series[0].data;
            if (dataArray.length > 0) {
              const lastVal = dataArray[dataArray.length - 1];
              this.aggregatedCount =
                typeof lastVal === 'object' && lastVal !== null
                  ? (lastVal.y ?? lastVal.value ?? 0)
                  : Number(lastVal) || 0;
            }
          } else {
            const lastVal = series[series.length - 1] || series[0];
            this.aggregatedCount =
              typeof lastVal === 'object' && lastVal !== null
                ? (lastVal.y ?? lastVal.value ?? 0)
                : Number(lastVal) || 0;
          }
        }
        this.cdr.markForCheck();
      },
      error: (err) => console.log(err),
    });
  }

  openProfile(item: any) {
    if (this.profileResponse) {
      const fieldDeps = [
        {
          trigger: 'onClick',
          condition: [
            {
              conditions: [
                {
                  type: 'modal',
                  targetType: 'form',
                  targetValue: FORM_ID.PROFILE,
                },
              ],
            },
          ],
        },
      ];
      const onClickDep = fieldDeps.find(
        (dep: any) => dep.trigger === 'onClick',
      );

      if (onClickDep && Array.isArray(onClickDep.condition)) {
        for (const condBlock of onClickDep.condition) {
          const modalCond = condBlock.conditions?.find(
            (c: any) =>
              c.type === 'modal' && c.targetType === 'form' && c.targetValue,
          );

          if (modalCond) {
            this.closeAll();
            this.activeModalContext = {
              nodeId: modalCond.targetValue,
              fullName: item.name,
              userEmail: item.email,
              mobileNumber: item.phone,
            };
            this.cdr.markForCheck();
            const modal = document.getElementById('topbar_profile_modal');
            if (modal) {
              modal.style.display = 'flex';
              modal.classList.add('show');
            }
            return;
          }
        }
      }
    }
  }

  setModalTitle(title: string) {
    this.modalTitle = title;
    this.cdr.markForCheck();
  }

  closeModal() {
    const modal = document.getElementById('topbar_profile_modal');
    if (modal) {
      modal.classList.remove('show');
      setTimeout(() => (modal.style.display = 'none'), 300);
    }
  }

  closeOnBackdropClick(event: MouseEvent) {
    const modal = document.getElementById('topbar_profile_modal');
    if (event.target === modal) {
      this.closeModal();
    }
  }

  openSettings() {
    this.closeAll();
    const modal = document.getElementById('topbar_modal_settings');
    if (modal) {
      modal.style.display = 'flex';
      modal.classList.add('show');
    }
  }

  closeSettingsModal(modalID: any) {
    const modal = document.getElementById(modalID);
    if (modal) {
      modal.classList.remove('show');
      setTimeout(() => (modal.style.display = 'none'), 300);
    }
    this.activeModalContext = null;
    this.cdr.markForCheck();
  }

  closeSettingsOnBackdropClick(event: MouseEvent, modalID: any) {
    const modal = document.getElementById(modalID);
    if (event.target === modal) {
      this.closeSettingsModal(modalID);
    }
  }

  onDarkModeToggle() {
    this.prefs.toggleTheme();
  }

  openTerms() {
    this.closeAll();
    const url = this.location.prepareExternalUrl(
      'assets/docs/terms-of-use/index.html',
    );
    window.open(url, '_blank');
  }

  contactSupport() {
    this.closeAll();
    window.location.href = 'mailto:support@eciso.ai';
  }

  logout() {
    let token = localStorage.getItem('accessToken');
    const performCleanupAndNavigate = () => {
      this.posthog.reset();
      localStorage.removeItem('lastVisitedUrl');
      localStorage.removeItem('accessToken');
      this.prefs.clearStoragePreservingTheme();
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }
      if ('caches' in window) {
        caches
          .keys()
          .then((keys) => keys.forEach((key) => caches.delete(key)))
          .catch((err) => console.error('Error clearing cache:', err));
      }
      this.router.navigate(['/login']);
    };

    if (token) {
      this.realAuth.logout(token).subscribe({
        next: (data) => {
          performCleanupAndNavigate();
        },
        error: (err) => {
          console.error('Error logging out via API', err);
          performCleanupAndNavigate();
        },
      });
    } else {
      performCleanupAndNavigate();
    }
  }

  get companyLogo() {
    return this.prefs.companyLogo();
  }
  get user() {
    return this.auth.currentUser();
  }
  get unread() {
    return this.notifs.unreadCount();
  }
  get isDark() {
    return this.prefs.resolvedTheme() === 'dark';
  }

  toggleWaffle(): void {
    this.waffleOpen.update((v) => !v);
    this.notifOpen.set(false);
    this.userMenuOpen = false;
    this.bookmarkMenuOpen.set(false);
  }
  toggleNotifs(event?: MouseEvent): void {
    event?.stopPropagation();
    const willOpen = !this.notifOpen();
    this.notifOpen.set(willOpen);
    if (willOpen) {
      this.notifs.loadData(FORM_ID.NOTIFICATIONS_LIST);
    }
    this.waffleOpen.set(false);
    this.userMenuOpen = false;
    this.bookmarkMenuOpen.set(false);
  }
  toggleUserMenu(): void {
    this.userMenuOpen = !this.userMenuOpen;
    this.waffleOpen.set(false);
    this.notifOpen.set(false);
    this.bookmarkMenuOpen.set(false);
  }
  toggleTheme(): void {
    this.prefs.toggleTheme();
  }

  toggleBookmarkMenu(event: MouseEvent): void {
    event.stopPropagation();
    const nextState = !this.bookmarkMenuOpen();
    this.bookmarkMenuOpen.set(nextState);
    if (nextState) {
      this.waffleOpen.set(false);
      this.notifOpen.set(false);
      this.userMenuOpen = false;
      this.loadBookmarks();
    }
  }

  loadBookmarks(): void {
    this.bookmarksLoading = true;
    this.cdr.markForCheck();
    this.cdr.detectChanges();

    const fetchBookmarksList = (email?: string) => {
      const formId = FORM_ID.BOOKMARKS;
      const options = {
        sort: '-createdAt',
        userID: email,
      };
      this.commonService.loadListWithOptions(formId, options).subscribe({
        next: (data) => {
          this.bookmarks = data?.data?.records || [];
          this.bookmarksLoading = false;
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading bookmarks:', err);
          this.bookmarks = [];
          this.bookmarksLoading = false;
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        },
      });
    };

    if (!this.profileResponse?.email) {
      let token = localStorage.getItem('accessToken');
      if (token) {
        this.realAuth.getProfile(token).subscribe({
          next: (data) => {
            this.profileResponse = data.data;
            this.isOrgLogoBroken = false;
            localStorage.setItem('pmSetup', this.profileResponse?.pmSetup);
            this.fetchProfilePhoto();
            this.fetchOrgProfilePhoto();
            fetchBookmarksList(this.profileResponse?.email);
          },
          error: (err) => {
            console.error('Error fetching profile in loadBookmarks:', err);
            fetchBookmarksList();
          },
        });
      } else {
        fetchBookmarksList();
      }
    } else {
      fetchBookmarksList(this.profileResponse.email);
    }
  }

  navigateBookmark(item: any, event: MouseEvent): void {
    event.stopPropagation();
    let url = item.link;
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    window.open(url, '_blank');
    this.bookmarkMenuOpen.set(false);
  }

  goToBookmarks(event: MouseEvent): void {
    event.stopPropagation();
    this.router.navigate(['/eciso/bookmarks']);
    this.bookmarkMenuOpen.set(false);
  }

  get isBookmarkWriteAccess(): boolean {
    const accessList = getRoleAccess(this.profileResponse);
    if (this.profileResponse && !accessList) {
      return true;
    }
    return (
      getAccessValue(this.profileResponse, 'my-workspace', 'my-bookmarks') ===
      'read_write'
    );
  }

  openAddBookmarkModal(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isBookmarkWriteAccess) {
      return;
    }
    this.bookmarkMenuOpen.set(false);
    const formID = FORM_ID.BOOKMARK_ADD;
    if (formID) {
      this.activeBookmarkModalContext = {
        nodeId: formID,
        recordID: 'add',
      };
      this.cdr.markForCheck();
      this.cdr.detectChanges();

      setTimeout(() => {
        const modalId =
          'open_modal_' +
          (this.activeBookmarkModalContext?.nodeId || 'default');
        const modal = document.getElementById(modalId);
        if (modal) {
          modal.classList.add('show');
        }
      }, 0);
    }
  }

  closeBookmarkModal(): void {
    const modalId =
      'open_modal_' + (this.activeBookmarkModalContext?.nodeId || 'default');
    const modal = document.getElementById(modalId);
    if (modal) {
      modal.classList.remove('show');
    }
    this.bookmarkModalRendererRef?.resetForms();
    this.activeBookmarkModalContext = null;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  closeBookmarkModalOnBackdropClick(event: MouseEvent): void {
    const modalId =
      'open_modal_' + (this.activeBookmarkModalContext?.nodeId || 'default');
    const modal = document.getElementById(modalId);
    if (event.target === modal) {
      this.closeBookmarkModal();
    }
  }

  setBookmarkModalTitle(title: any): void {
    this.bookmarkModalTitle = title;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  openCurrentBookmarkModal(): void {
    const modalId =
      'open_modal_' + (this.activeBookmarkModalContext?.nodeId || 'default');
    const modal = document.getElementById(modalId);
    if (modal) {
      modal.classList.add('show');
    }
  }

  onBookmarkModalFormSaved(event: any): void {
    this.loadBookmarks();
    this.bookmarkModalRendererRef?.resetForms();
    this.closeBookmarkModal();
  }

  getFavicon(url: string): string {
    const domain = this.getDomain(url);
    if (this.faviconCache.has(domain)) {
      return this.faviconCache.get(domain)!;
    }
    const google = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    this.faviconCache.set(domain, google);
    return google;
  }

  onFaviconLoad(event: Event, url: string): void {
    const img = event.target as HTMLImageElement;
    const domain = this.getDomain(url);
    if (img.naturalWidth <= 32 && img.naturalHeight <= 32) {
      this.setDefault(img, domain);
      return;
    }
    const tester = new Image();
    tester.onload = () => {
      if (tester.naturalWidth <= 16 && tester.naturalHeight <= 16) {
        this.setDefault(img, domain);
      }
    };
    tester.onerror = () => {
      this.setDefault(img, domain);
    };
    tester.src = `https://icons.duckduckgo.com/ip3/${domain}.ico`;
  }

  onFaviconError(event: Event, url: string): void {
    const img = event.target as HTMLImageElement;
    const domain = this.getDomain(url);
    this.setDefault(img, domain);
  }

  setDefault(img: HTMLImageElement, domain: string): void {
    const fallback = 'assets/media/data-view/integration.png';
    img.src = fallback;
    this.faviconCache.set(domain, fallback);
  }

  getDomain(url: string): string {
    if (!url) return '';
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  closeAll(): void {
    this.waffleOpen.set(false);
    this.notifOpen.set(false);
    this.userMenuOpen = false;
    this.bookmarkMenuOpen.set(false);
  }

  closeUserMenu(): void {
    this.userMenuOpen = false;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.userMenuOpen = false;
    this.bookmarkMenuOpen.set(false);
    this.notifOpen.set(false);
  }

  openEditLogoModal(): void {
    this.stagedLogoFile = null;
    this.stagedLogoUrl = null;
    this.isLogoDragOver = false;
    this.showEditLogoModal = true;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  closeEditLogoModal(): void {
    this.showEditLogoModal = false;

    const modal = document.getElementById('logo_photo_modal');
    if (modal) {
      modal.classList.remove('show');
      modal.classList.remove('open');
      modal.style.display = '';

      const backdrops = document.querySelectorAll('.modal-backdrop');
      backdrops.forEach((backdrop) => backdrop.remove());

      document.body.style.overflow = '';
    }

    if (this.stagedLogoUrl) {
      URL.revokeObjectURL(this.stagedLogoUrl);
      this.stagedLogoUrl = null;
    }
    this.stagedLogoFile = null;
    this.isLogoDragOver = false;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  closeLogoOnBackdropClick(event: MouseEvent, modalId: string): void {
    const modal = document.getElementById(modalId);
    if (event.target === modal) {
      this.closeEditLogoModal();
    }
  }

  triggerLogoFileInput(): void {
    if (this.modalLogoFileInput) {
      this.modalLogoFileInput.nativeElement.click();
    }
  }

  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.handleLogoFile(input.files[0]);
      input.value = '';
    }
  }

  onLogoDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isLogoDragOver = true;
  }

  onLogoDragLeave(): void {
    this.isLogoDragOver = false;
  }

  onLogoFileDropped(event: DragEvent): void {
    event.preventDefault();
    this.isLogoDragOver = false;
    const file = event.dataTransfer?.files[0];
    if (file) {
      this.handleLogoFile(file);
    }
  }

  handleLogoFile(file: File): void {
    const allowedTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/svg+xml',
    ];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['png', 'jpg', 'jpeg', 'svg'];

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension || '')
    ) {
      this.toaster.error(
        'Invalid file format. Only PNG, JPG, JPEG, and SVG are allowed.',
        'Error',
        {
          messageClass: 'addMessage',
          titleClass: 'addCss',
        },
      );
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      this.toaster.error('File size exceeds the 2MB limit.', 'Error', {
        messageClass: 'addMessage',
        titleClass: 'addCss',
      });
      return;
    }

    if (this.stagedLogoUrl) {
      URL.revokeObjectURL(this.stagedLogoUrl);
    }
    this.stagedLogoFile = file;
    this.stagedLogoUrl = URL.createObjectURL(file);
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  removeSelectedLogo(event: Event): void {
    event.stopPropagation();
    if (this.stagedLogoUrl) {
      URL.revokeObjectURL(this.stagedLogoUrl);
    }
    this.stagedLogoFile = null;
    this.stagedLogoUrl = null;
    this.orgProfilePhotoUrl = null;
    this.isOrgLogoBroken = true;
    this.prefs.updateCompanyLogo({ ...this.companyLogo, dataUrl: null });
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  saveLogo(): void {
    if (!this.stagedLogoFile) {
      this.toaster.error('No changes to save.', 'Warning', {
        messageClass: 'addMessage',
        titleClass: 'addCss',
      });
      return;
    }

    this.isLogoUploading = true;
    let token = localStorage.getItem('accessToken');
    if (!token) {
      this.toaster.error('Authentication token missing.', 'Error', {
        messageClass: 'addMessage',
        titleClass: 'addCss',
      });
      this.isLogoUploading = false;
      return;
    }

    this.realAuth
      .uploadOrganizationProfilePhoto(token, this.stagedLogoFile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isLogoUploading = false;
          this.toaster.success(
            'Organization profile photo updated successfully',
            'Success',
            {
              messageClass: 'addMessage',
              titleClass: 'addCss',
            },
          );
          if (this.stagedLogoUrl) {
            this.prefs.updateCompanyLogo({
              ...this.companyLogo,
              dataUrl: this.stagedLogoUrl,
            });
          }
          this.getProfileDetails();
          this.closeEditLogoModal();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isLogoUploading = false;
          this.toaster.error(
            err?.error?.message ||
              'Failed to upload organization profile photo',
            'Error',
            {
              messageClass: 'addMessage',
              titleClass: 'addCss',
            },
          );
          this.cdr.detectChanges();
        },
      });
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: any): void {
    this.closeEditLogoModal();
    this.closeBookmarkModal();
  }
}
